import {
  CoreObject,
  DomainGraphInterface,
  MaybeCoreObject,
  ObjectType,
} from 'app-core/framework/CoreObjectConstants';
import {
  doesObjectTypeHaveInterface,
  getModelTypeConstantForObjectType,
  getObjectType,
  getObjectTypesWithInterface,
  isCoreObject,
} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {
  DEFAULT_STUDYABLE_CONFIGURATION,
  StudyableConfiguration,
  getPlatformConfigForItemType,
  isStudyableConfiguration,
} from 'app-core/framework/PlatformConfiguration';
import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';

/**
 * TODO: Don't add to this list. When item types are made fully Studyable, remove from this list.
 * @package
 */
export const TEMPORARY_STUDYABLE_ITEM_TYPE_IGNORE_LIST: Set<ObjectType> = new Set([]);

export function isStudyableObjectType(objectType: ObjectType): boolean {
  return (
    doesObjectTypeHaveInterface(objectType, DomainGraphInterface.STUDYABLE) &&
    !TEMPORARY_STUDYABLE_ITEM_TYPE_IGNORE_LIST.has(objectType)
  );
}

export function getStudyableModelTypes(): Array<ModelTypeConstants | SequenceSubtype> {
  return [
    ...Array.from(getObjectTypesWithInterface(DomainGraphInterface.STUDYABLE))
      .filter((objectType) => isStudyableObjectType(objectType))
      .map((objectType) => getModelTypeConstantForObjectType(objectType))
      // Oligo conjugate and oligo duplex types are represented by ModelTypeConstants.BASIC_FOLDER_ITEM
      // on the frontend so we need to filter out the "DO_NOT_USE" placeholder types
      .filter(
        (modelType) =>
          modelType !== ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE &&
          modelType !== ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX &&
          modelType !== ModelTypeConstants._DO_NOT_USE_ANTIBODY
      ),
    // Recipe tasks are not integrated with the core object framework, so we explicitly
    // enumerate those types, as they do actually support studyable.
    ...(isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN})
      ? [
          ModelTypeConstants.RECIPE_RUN_TASK,
          ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
          ModelTypeConstants.RECIPE_STEP_TASK,
        ]
      : []),
    // Assay tasks are not integrated with the core object framework, so we explicitly
    // enumerate those types, as they do actually support studyable.
    ...(isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN})
      ? [
          ModelTypeConstants.ASSAY_RUN_TASK,
          ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
          ModelTypeConstants.ASSAY_STEP_TASK,
        ]
      : []),
  ];
}

function getPlatformConfiguration(item: CoreObject): StudyableConfiguration {
  const maybeConfig = getPlatformConfigForItemType(DomainGraphInterface.STUDYABLE, getObjectType(item));
  return isStudyableConfiguration(maybeConfig) ? maybeConfig : DEFAULT_STUDYABLE_CONFIGURATION;
}

export function isItemStudyable(item: MaybeCoreObject): item is CoreObject {
  return (
    isCoreObject(item) &&
    isStudyableObjectType(getObjectType(item)) &&
    getPlatformConfiguration(item).isPlatformCapabilityEnabledForItemInstance(item)
  );
}

export function isItemTypeStudyable(item: MaybeCoreObject): boolean {
  return isCoreObject(item) && isStudyableObjectType(getObjectType(item));
}

export function getStudyableItems(items: Array<MaybeCoreObject>): Array<CoreObject> {
  return items.filter((item): item is CoreObject => isItemStudyable(item));
}

export function isItemAutoLinkable(item: MaybeCoreObject): boolean {
  return (
    isCoreObject(item) && getModelTypeConstantForObjectType(getObjectType(item)) === ModelTypeConstants.ENTRY
  );
}
